import React, { useContext, useEffect, useState } from "react";

import { CheckCircleFilled, CopyOutlined, KeyOutlined, MobileOutlined, QrcodeOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Steps } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";

import { RoleContext } from "../context/RoleContext";
import CreateCredentialService from "../services/CreateCredentialService";

interface AddNewDeviceModalProps {
  visible: boolean;
  onClose: () => void;
}

const CODE_TTL_SECONDS = 60;

const copyToClipboard = (text: string, successMessage: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(successMessage))
    .catch(() => toast.error("Failed to copy to clipboard"));
};

const formatCredentialCode = (code: string): string => code.replace(/(.{3})(.{3})(.{3})/, "$1-$2-$3");

const AddNewDeviceModal: React.FC<AddNewDeviceModalProps> = ({ visible, onClose }) => {
  const { dfnsToken } = useContext(RoleContext);
  const [loading, setLoading] = useState(false);
  const [credentialCode, setCredentialCode] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const qrCodeUrl = `${baseUrl}/credentials/code`;

  // Countdown timer for the credential code's 60-second window.
  useEffect(() => {
    if (!credentialCode) return;
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [credentialCode, secondsLeft]);

  // Reset when the modal closes so the next open starts fresh.
  useEffect(() => {
    if (!visible) {
      setCredentialCode(null);
      setSecondsLeft(0);
      setLoading(false);
    }
  }, [visible]);

  const handleCreateCredentialCode = async () => {
    setCredentialCode(null);
    setSecondsLeft(0);
    try {
      setLoading(true);

      if (!dfnsToken) {
        toast.error("DFNS token not found. Please re-login.");
        return;
      }

      const expirationTime = new Date(Date.now() + CODE_TTL_SECONDS * 1000).toISOString();

      const { initiateResponse, error: initError } = await CreateCredentialService.initiateCreateCredentialCode(expirationTime, dfnsToken);
      if (initError) {
        console.error(initError);
        toast.error("We couldn't start device setup. Please try again.");
        return;
      }

      const { completeResponse, error: completeError } = await CreateCredentialService.completeCreateCredentialCode(
        dfnsToken,
        initiateResponse.challenge,
        initiateResponse.requestBody
      );

      if (completeError) {
        console.error(completeError);
        toast.error("We couldn't finish device setup. Please try again.");
      } else {
        setCredentialCode(completeResponse?.credentialCodeResponse?.code);
        setSecondsLeft(CODE_TTL_SECONDS);
        toast.success("Credential code created!");
      }
    } catch (error: any) {
      console.error(error);
      toast.error("We couldn't complete this action. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const hasCode = !!credentialCode;
  const expired = hasCode && secondsLeft <= 0;
  const currentStep = !hasCode ? 0 : expired ? 1 : 2;

  return (
    <Modal title="Add New Device" open={visible} onCancel={onClose} footer={null} maskClosable={false} width={640}>
      {/* Intro */}
      <Alert
        type="info"
        showIcon
        className="mb-5"
        message="What this does"
        description="Set up a passkey on another device (your phone, a second laptop) without logging in there. First open the link below on the new device, then generate a one-time code here and enter it there."
      />

      {/* Progress indicator */}
      <Steps
        size="small"
        current={currentStep}
        className="mb-6"
        items={[
          { title: "Open on new device", icon: <QrcodeOutlined /> },
          { title: "Generate code", icon: <KeyOutlined /> },
          { title: "Enter code there", icon: <MobileOutlined /> },
        ]}
      />

      {/* Step 1 — open on new device */}
      <div className="border rounded-lg p-4 mb-4">
        <h3 className="text-base font-semibold mb-2">Step 1 — Open this link on the new device</h3>
        <p className="text-sm text-gray-600 mb-4">
          Scan the QR code below, or copy the URL and open it in the browser on the new device. Keep that page open and ready before you generate the
          code in Step 2 — the code is only valid for {CODE_TTL_SECONDS} seconds.
        </p>
        <div className="flex flex-col items-center">
          <QRCodeSVG value={qrCodeUrl} size={180} bgColor="#ffffff" fgColor="#000000" level="M" includeMargin={true} />
          <div className="mt-3 w-full flex items-center justify-between gap-2 bg-gray-50 rounded px-3 py-2">
            <span className="text-xs text-gray-700 break-all">{qrCodeUrl}</span>
            <Button type="text" icon={<CopyOutlined />} onClick={() => copyToClipboard(qrCodeUrl, "URL copied to clipboard")} aria-label="Copy URL" />
          </div>
        </div>
      </div>

      {/* Step 2 — generate code (or display it) */}
      {!hasCode && (
        <div className="border rounded-lg p-4 mb-4">
          <h3 className="text-base font-semibold mb-2">Step 2 — Generate a one-time code</h3>
          <p className="text-sm text-gray-600 mb-4">
            Once the new device is on the link from Step 1, click below to create a credential code valid for {CODE_TTL_SECONDS} seconds. You'll be
            asked to approve the request with your current passkey.
          </p>
          <Button type="primary" onClick={handleCreateCredentialCode} loading={loading} block size="large">
            Generate Credential Code
          </Button>
        </div>
      )}

      {hasCode && (
        <div className="border rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircleFilled className="text-green-500" />
            <h3 className="text-base font-semibold mb-0">Step 2 — Code generated</h3>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl font-mono font-bold bg-white px-4 py-2 rounded border tracking-widest">
                {formatCredentialCode(credentialCode!)}
              </span>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(credentialCode!, "Code copied to clipboard")}
                aria-label="Copy code"
              />
            </div>
            {expired ? (
              <Alert type="warning" showIcon message="This code has expired. Generate a new one to continue." className="mt-3" />
            ) : (
              <p className="text-sm text-gray-600 mt-2">
                Valid for <span className="font-semibold">{secondsLeft}s</span>
              </p>
            )}
          </div>

          {expired && (
            <Button onClick={handleCreateCredentialCode} loading={loading} className="mt-3" block>
              Generate new code
            </Button>
          )}
        </div>
      )}

      {/* Step 3 — enter code on new device */}
      {hasCode && !expired && (
        <div className="border rounded-lg p-4 mb-2">
          <h3 className="text-base font-semibold mb-2">Step 3 — Enter the code on the new device</h3>
          <p className="text-sm text-gray-600">
            Switch to the new device. Paste or type the code shown above. The device will then prompt you to register a passkey (Touch ID, Face ID,
            security key, etc.). Once approved, the new device is set up.
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end mt-4 pt-3 border-t">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
};

export default AddNewDeviceModal;
